"use client"

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm, type CrudField, type CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'
import { createCrud } from '@open-mercato/ui/backend/utils/crud'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useOrganizationScopeDetail } from '@open-mercato/shared/lib/frontend/useOrganizationScope'

export default function CreateTrainPlanPage() {
  const t = useT()
  const router = useRouter()
  const { organizationId, tenantId } = useOrganizationScopeDetail()

  const fields = React.useMemo<CrudField[]>(() => [
    {
      id: 'planNumber',
      label: t('train_planning.plans.fields.planNumber', 'Plan Number'),
      type: 'text',
      required: true,
      placeholder: 'TP-2026-00001',
    },
    {
      id: 'name',
      label: t('train_planning.plans.fields.name', 'Name'),
      type: 'text',
      placeholder: 'Katowice → Gdańsk Kontenerowy',
    },
    {
      id: 'locomotiveId',
      label: t('train_planning.plans.fields.locomotiveId', 'Locomotive ID'),
      type: 'text',
      required: true,
      placeholder: 'UUID of locomotive',
    },
    {
      id: 'fromStationId',
      label: t('train_planning.plans.fields.fromStationId', 'Departure Station ID'),
      type: 'text',
      placeholder: 'UUID of departure station',
    },
    {
      id: 'toStationId',
      label: t('train_planning.plans.fields.toStationId', 'Arrival Station ID'),
      type: 'text',
      placeholder: 'UUID of arrival station',
    },
    {
      id: 'routeSegmentId',
      label: t('train_planning.plans.fields.routeSegmentId', 'Route Segment ID'),
      type: 'text',
      placeholder: 'UUID of route segment',
    },
    {
      id: 'departureDate',
      label: t('train_planning.plans.fields.departureDate', 'Departure Date'),
      type: 'date',
    },
    {
      id: 'notes',
      label: t('train_planning.plans.fields.notes', 'Notes'),
      type: 'textarea',
    },
  ], [t])

  const groups = React.useMemo<CrudFormGroup[]>(() => [
    {
      id: 'identification',
      title: t('train_planning.plans.groups.identification', 'Identification'),
      fields: ['planNumber', 'name', 'locomotiveId'],
    },
    {
      id: 'route',
      title: t('train_planning.plans.groups.route', 'Route'),
      fields: ['fromStationId', 'toStationId', 'routeSegmentId', 'departureDate'],
    },
    {
      id: 'other',
      title: t('train_planning.plans.groups.other', 'Other'),
      fields: ['notes'],
    },
  ], [t])

  return (
    <Page>
      <PageBody>
        <CrudForm
          title={t('train_planning.plans.create.title', 'New Train Plan')}
          backHref="/backend/train-planning"
          fields={fields}
          groups={groups}
          submitLabel={t('train_planning.plans.create.submit', 'Create Train Plan')}
          cancelHref="/backend/train-planning"
          onSubmit={async (values) => {
            const payload = {
              ...values,
              organizationId,
              tenantId,
            }
            const { result: created } = await createCrud<{ id?: string }>('train_planning/train-plans', payload)
            flash(t('train_planning.plans.flash.created', 'Train plan created'), 'success')
            router.push(`/backend/train-planning/${created?.id}`)
          }}
        />
      </PageBody>
    </Page>
  )
}
